import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { TrackOticAuthProvider } from "@/context/TrackOticAuthContext";

// Portal Picker (the one and only public landing page)
import PortalPickerPage from "@/pages/PortalPickerPage";
import SetPasswordPage from "@/pages/SetPasswordPage";

// Control Portal (Track0tic Admin)
import ControlLayout from "@/pages/control/ControlLayout";
import ControlDashboard from "@/pages/control/ControlDashboard";
import TenantListPage from "@/pages/control/TenantListPage";
import TenantDetailPage from "@/pages/control/TenantDetailPage";
import TenantLocationsPage from "@/pages/control/TenantLocationsPage";
import TenantUsersPage from "@/pages/control/TenantUsersPage";
import TenantIntegrationsPage from "@/pages/control/TenantIntegrationsPage";
import TenantBillingPage from "@/pages/control/TenantBillingPage";
import GlobalAuditPage from "@/pages/control/GlobalAuditPage";
import GlobalAlertsPage from "@/pages/control/GlobalAlertsPage";
import GlobalBillingPage from "@/pages/control/GlobalBillingPage";
import SettingsPage from "@/pages/control/SettingsPage";
import DeviceInventoryPage from "@/pages/control/DeviceInventoryPage";
import DeviceDetailPage from "@/pages/control/DeviceDetailPage";
import DeviceRegistryPage from "@/pages/control/DeviceRegistryPage";
import DeviceDispatchPage from "@/pages/control/DeviceDispatchPage";
import PlanBuilderPage from "@/pages/control/PlanBuilderPage";
import FleetManagementPage from "@/pages/control/FleetManagementPage";
import ShipmentsPage from "@/pages/control/ShipmentsPage";
import ColdChainPage from "@/pages/control/ColdChainPage";
import DeviceProvisioningPage from "@/pages/control/DeviceProvisioningPage";
import OnboardingWizardPage from "@/pages/control/OnboardingWizardPage";
import PlatformUsersPage from "@/pages/control/PlatformUsersPage";

// Admin pages (platform admin — Phase 7 refactored URLs with legacy redirects)
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminDevicesPage from "@/pages/admin/AdminDevicesPage";
import AdminDeviceProfilePage from "@/pages/admin/AdminDeviceProfilePage";
import AdminQCPage from "@/pages/admin/AdminQCPage";
import DeviceLabelsPage from "@/pages/admin/DeviceLabelsPage";
import IssuePassportsPage from "@/pages/admin/IssuePassportsPage";
import ReceiveDevicesPage from "@/pages/admin/ReceiveDevicesPage";
import AdminAlertsPage from "@/pages/admin/AdminAlertsPage";
import ApprovalsPage from "@/pages/admin/ApprovalsPage";
import { LegacyRedirect } from "@/pages/admin/LegacyRedirect";
import AdminQrProfilesPage from "@/pages/admin/qr/AdminQrProfilesPage";
import QrOperationsPage from "@/pages/ops/QrOperationsPage";
import AdminAllocationPage from "@/pages/admin/AdminAllocationPage";
import AdminTripsPage from "@/pages/admin/AdminTripsPage";
import AdminCustomersPage from "@/pages/admin/AdminCustomersPage";
import NewCustomerVerticalPage from "@/pages/admin/NewCustomerVerticalPage";
import NewCustomerFormPage from "@/pages/admin/NewCustomerFormPage";
import AdminCustomerDetailPage from "@/pages/admin/AdminCustomerDetailPage";
import AdminUsersRolesPage from "@/pages/admin/AdminUsersRolesPage";
import AdminJourneyTemplatesPage from "@/pages/admin/AdminJourneyTemplatesPage";
import JourneySharingPage from "@/pages/admin/JourneySharingPage";
import PassportContentPage from "@/pages/admin/PassportContentPage";
import AdminPassportsPage from "@/pages/admin/AdminPassportsPage";
import AdminCompliancePage from "@/pages/admin/AdminCompliancePage";
import AdminReportsPage from "@/pages/admin/AdminReportsPage";

// Admin — Phase 2b Staff RBAC pages
import StaffUsersPage from "@/pages/admin/staff/StaffUsersPage";
import ConsentGrantsPage from "@/pages/admin/staff/ConsentGrantsPage";
import AuditLogPage from "@/pages/admin/staff/AuditLogPage";
import SettingsRefPage from "@/pages/admin/staff/SettingsRefPage";

import ScanPage from "@/pages/ScanPage";
import StickerPassportPage from "@/pages/public/StickerPassportPage";

// Customer Ops Portal (Tenant Operations)
import OpsLayout from "@/pages/ops/OpsLayout";
import OpsDashboard from "@/pages/ops/OpsDashboard";
import WorkOrdersPage from "@/pages/ops/WorkOrdersPage";
import WorkOrderDetailPage from "@/pages/ops/WorkOrderDetailPage";
import LiveTrackingPage from "@/pages/ops/LiveTrackingPage";
import DeviceHealthPage from "@/pages/ops/DeviceHealthPage";
import OpsAlertsPage from "@/pages/ops/OpsAlertsPage";
import OpsReportsPage from "@/pages/ops/OpsReportsPage";
import OpsTelemetryLogsPage from "@/pages/ops/OpsTelemetryLogsPage";
import OpsUsersPage from "@/pages/ops/OpsUsersPage";
import TripsPage from "@/pages/ops/TripsPage";
import OpsColdChainPage from "@/pages/ops/OpsColdChainPage";
import LabelInventoryPage from "@/pages/ops/LabelInventoryPage";
import DispatcherWorkflowPage from "@/pages/ops/DispatcherWorkflowPage";
import PassportViewerPage from "@/pages/ops/PassportViewerPage";
import PassportStockPage from "@/pages/ops/PassportStockPage";
import DevicePage from "@/pages/qr/DevicePage";
import QrOtpLoginPage from "@/pages/qr/QrOtpLoginPage";
import PassportPageRouter, { CertificateRedirect } from "@/pages/qr/PassportPage";
import HubTimeslicePage from "@/pages/ops/HubTimeslicePage";
import DeliveryFlowPage from "@/pages/ops/DeliveryFlowPage";
import OpsOnboardingWizardPage from "@/pages/ops/OpsOnboardingWizardPage";

// Phase 36a — Axis Bank CIT portal (Wave 1)
import OpsVansPage from "@/pages/ops/OpsVansPage";
import OpsCitVendorsPage from "@/pages/ops/OpsCitVendorsPage";
import OpsDriversPage from "@/pages/ops/OpsDriversPage";

// Public QR / Tracking — unauthenticated, but ONLY reached via tokenized URLs.
import PublicTrackingPage from "@/pages/public/PublicTrackingPage";
import DeliveryConfirmPage from "@/pages/public/DeliveryConfirmPage";
import DeliveryVerificationPage from "@/pages/DeliveryVerificationPage";

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TrackOticAuthProvider>
          <div className="App min-h-screen bg-background text-foreground">
            <BrowserRouter>
              <Routes>
                {/* Single public landing page — Unified Login */}
                <Route path="/" element={<PortalPickerPage />} />

                {/* Legacy portal-specific login URLs redirect to the unified login. */}
                <Route path="/control/login" element={<Navigate to="/" replace />} />
                <Route path="/ops/login" element={<Navigate to="/" replace />} />

                {/* Account self-service (any logged-in user can set/change password) */}
                <Route path="/account/set-password" element={<SetPasswordPage />} />

                {/* Platform Admin Panel — Phase 7 refactored structure */}
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  {/* 1 Devices */}
                  <Route path="devices/receive" element={<ReceiveDevicesPage />} />
                  <Route path="devices" element={<AdminDevicesPage />} />
                  <Route path="devices/:deviceId" element={<AdminDeviceProfilePage />} />
                  <Route path="qc" element={<AdminQCPage />} />
                  <Route path="device-labels" element={<DeviceLabelsPage />} />
                  {/* 2 Tenants */}
                  <Route path="tenants" element={<AdminCustomersPage />} />
                  <Route path="tenants/new" element={<NewCustomerVerticalPage />} />
                  <Route path="tenants/new/:verticalCode" element={<NewCustomerFormPage />} />
                  <Route path="tenants/:tenantId" element={<AdminCustomerDetailPage />} />
                  <Route path="tenants/:tenantId/sharing" element={<JourneySharingPage />} />
                  <Route path="tenants/:tenantId/passport-content" element={<PassportContentPage />} />
                  <Route path="users" element={<AdminUsersRolesPage />} />
                  <Route path="journeys" element={<AdminJourneyTemplatesPage />} />
                  <Route path="consent-grants" element={<ConsentGrantsPage />} />
                  {/* 3 Sale & supply */}
                  <Route path="allocation" element={<AdminAllocationPage />} />
                  <Route path="issue-passports" element={<IssuePassportsPage />} />
                  <Route path="passports/:passportId" element={<AdminPassportsPage />} />
                  <Route path="qr-profiles" element={<AdminQrProfilesPage />} />
                  {/* 4 Operations */}
                  <Route path="trips" element={<AdminTripsPage />} />
                  <Route path="trips/:tripId" element={<AdminTripsPage />} />
                  <Route path="alerts" element={<AdminAlertsPage />} />
                  <Route path="approvals" element={<ApprovalsPage />} />
                  <Route path="compliance" element={<AdminCompliancePage />} />
                  {/* 5 Admin */}
                  <Route path="staff-users" element={<StaffUsersPage />} />
                  <Route path="reports" element={<AdminReportsPage />} />
                  <Route path="audit-log" element={<AuditLogPage />} />
                  <Route path="settings-ref" element={<SettingsRefPage />} />
                  {/* Phase 7 redirects — every old bookmark keeps working */}
                  <Route path="qr-labels" element={<Navigate to="/admin/device-labels" replace />} />
                  <Route path="qr-passports" element={<Navigate to="/admin/issue-passports" replace />} />
                  <Route path="qr-exceptions" element={<Navigate to="/admin/alerts" replace />} />
                  <Route path="staff-approvals" element={<Navigate to="/admin/approvals" replace />} />
                  <Route path="passports" element={<Navigate to="/admin/issue-passports?tab=journeys" replace />} />
                  <Route path="customers" element={<Navigate to="/admin/tenants" replace />} />
                  <Route path="customers/new" element={<Navigate to="/admin/tenants/new" replace />} />
                  <Route path="customers/new/:verticalCode" element={<LegacyRedirect to="/admin/tenants/new/:verticalCode" />} />
                  <Route path="customers/:tenantId" element={<LegacyRedirect to="/admin/tenants/:tenantId" />} />
                  <Route path="customers/:tenantId/sharing" element={<LegacyRedirect to="/admin/tenants/:tenantId/sharing" />} />
                  <Route path="customers/:tenantId/passport-content" element={<LegacyRedirect to="/admin/tenants/:tenantId/passport-content" />} />
                </Route>

                <Route path="/control" element={<ControlLayout />}>
                  <Route index element={<Navigate to="/control/dashboard" replace />} />
                  <Route path="dashboard" element={<ControlDashboard />} />
                  <Route path="tenants" element={<TenantListPage />} />
                  <Route path="tenants/:tenantId" element={<TenantDetailPage />} />
                  <Route path="tenants/:tenantId/locations" element={<TenantLocationsPage />} />
                  <Route path="tenants/:tenantId/users" element={<TenantUsersPage />} />
                  <Route path="tenants/:tenantId/integrations" element={<TenantIntegrationsPage />} />
                  <Route path="tenants/:tenantId/billing" element={<TenantBillingPage />} />
                  <Route path="audit" element={<GlobalAuditPage />} />
                  <Route path="alerts" element={<GlobalAlertsPage />} />
                  <Route path="billing" element={<GlobalBillingPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="users" element={<PlatformUsersPage />} />
                  <Route path="plans" element={<PlanBuilderPage />} />
                  <Route path="devices" element={<DeviceInventoryPage />} />
                  <Route path="devices/:deviceId" element={<DeviceDetailPage />} />
                  <Route path="device-registry" element={<DeviceRegistryPage />} />
                  <Route path="device-dispatch" element={<DeviceDispatchPage />} />
                  <Route path="device-provisioning" element={<DeviceProvisioningPage />} />
                  <Route path="onboarding" element={<OnboardingWizardPage />} />
                  <Route path="fleet" element={<FleetManagementPage />} />
                  <Route path="shipments" element={<ShipmentsPage />} />
                  <Route path="cold-chain" element={<ColdChainPage />} />
                </Route>

                {/* Customer Ops Portal (Tenant Operations) */}
                <Route path="/ops" element={<OpsLayout />}>
                  <Route index element={<Navigate to="/ops/dashboard" replace />} />
                  <Route path="dashboard" element={<OpsDashboard />} />
                  <Route path="work-orders" element={<WorkOrdersPage />} />
                  <Route path="work-orders/:id" element={<WorkOrderDetailPage />} />
                  <Route path="tracking" element={<LiveTrackingPage />} />
                  <Route path="devices" element={<DeviceHealthPage />} />
                  <Route path="alerts" element={<OpsAlertsPage />} />
                  <Route path="reports" element={<OpsReportsPage />} />
                  <Route path="telemetry" element={<OpsTelemetryLogsPage />} />
                  <Route path="trips" element={<TripsPage />} />
                  <Route path="users" element={<OpsUsersPage />} />
                  <Route path="cold-chain" element={<OpsColdChainPage />} />
                  <Route path="labels" element={<LabelInventoryPage />} />
                  <Route path="dispatcher" element={<DispatcherWorkflowPage />} />
                  <Route path="passport" element={<PassportViewerPage />} />
                  <Route path="passport-stock" element={<PassportStockPage />} />
                  <Route path="qr-ops" element={<QrOperationsPage />} />
                  <Route path="hub" element={<HubTimeslicePage />} />
                  <Route path="delivery" element={<DeliveryFlowPage />} />
                  <Route path="onboarding" element={<OpsOnboardingWizardPage />} />
                  {/* Phase 36a — Axis Bank CIT portal (Wave 1) */}
                  <Route path="vans" element={<OpsVansPage />} />
                  <Route path="cit-vendors" element={<OpsCitVendorsPage />} />
                  <Route path="drivers" element={<OpsDriversPage />} />
                </Route>

                {/* Public QR-token pages (cold-chain transparency for end recipients) */}
                <Route path="/d/:token" element={<DevicePage />} />
                <Route path="/d-login" element={<QrOtpLoginPage />} />
                <Route path="/verify/:qrCode" element={<DeliveryVerificationPage />} />
                <Route path="/track/:trackingCode" element={<PublicTrackingPage />} />
                <Route path="/confirm/:token" element={<DeliveryConfirmPage />} />
                <Route path="/p/:passportUuid" element={<PassportPageRouter />} />
                <Route path="/p/:passportUuid/certificate.pdf" element={<CertificateRedirect />} />
                <Route path="/c/:passportUuid" element={<StickerPassportPage />} />
                <Route path="/scan" element={<ScanPage />} />

                {/* Everything else (legacy marketing pages, /app/*, /login, /dashboard, etc.)
                    funnels back to the Portal Picker. */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster position="top-right" richColors duration={2000} />
            </BrowserRouter>
          </div>
        </TrackOticAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
